import {
  LineInput,
  PURCHASE_MSG,
  RECEIVED_KINDS,
  RECEIVED_KIND_LABEL,
  RECEIVED_LINE_KIND_LABEL,
  RECEIVED_LINE_KIND_SELECTABLE,
  RECEIVED_MSG,
  RECEIVED_READ_MSG,
  ROW_ROLE_HELP,
  receivedCheckAll,
  receivedCheckOnly,
  receivedKindOption,
  receivedLineAmount,
  receivedLineCheck,
  type ReceivedCandidate,
  type ReceivedDocument,
  type ReceivedImportResult,
  type ReceivedKind,
  type ReceivedLineKind,
  type ReceivedReadLine,
  type ReceivedReadView,
  type Suggest,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { ApiError, api, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { DateInput } from "../../components/DateInput.jsx";
import { Trunc } from "../../components/GroupList.jsx";
import { CheckIcon, InfoIcon, ScanIcon, SpinIcon, WarnIcon, WidenIcon } from "../../components/icons.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { Picker, type Cand, type PickTab } from "../../components/Picker.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { ErrorText, Field, Help, type ToastFn } from "../../components/ui.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { uses } from "../../core/uses.js";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";
import { useNarrow } from "../../lib/useNarrow.js";
import { useWide } from "../../pages/schedule/hooks.js";
import { useSplit } from "../../components/SplitPanes.jsx";
import { OriginalPane, type Zoom } from "./OriginalPane.jsx";

/**
 * 読み取りの確認の引き出し「AI読み取り結果」（受け取った書類の2回目、../eigyouryoku/docs/13 §4.3、R2 §5-2。S3＝§4.5・依頼文 R3 §3、絵＝received-read/Drawer・Phone）。
 *   右から・画面幅いっぱい（原本の引き出しと同じ .vdrawer）。開くたびに GET …/read（読み取った内容は API が返すたびに直す＝画面は直さない）。
 *   上の帯：走査線のアイコン＋「AI読み取り結果」・書類の名前・「再読み取り」（窓で確かめる）・「閉じる」。
 *     幅 767px 以下は2段（題と「閉じる」／名前と ⋮＝再読み取り・原本を開く・ダウンロード）
 *   左＝原本（OriginalPane：どの幅でもページ画像・ツールバーで拡大縮小）｜境（6px）｜右＝読み取った内容（直せる）
 *     境：ドラッグ（Pointer Events）・← →（5%）・ダブルクリックで既定の 40／60・30〜70%・端末に覚える（localStorage gb.rd.split）。
 *       ドラッグだけにしない＝原本のツールバーの「原本を広く／内容を広く」（60／40 ⇄ 40／60）
 *     1100px 未満は上下（原本が上・境は横・↑↓ でも）、767px 以下は原本をたたんだ行「原本 › P.n／m」（押すと開く・2本指で拡大）
 *     ① 書類（提案が今の書類と違えば「注文書のようです」＋「注文書にする」）② 差出人（{customer}と違えば帯「…は{customer}と異なります」。案件は変えない）
 *     ③ 頭の欄（日付＝DateInput・{subject}・番号・{site}・{period}＝文字のまま）④ 行（No・名称・🔍・規格・数量・単位・単価・金額・備考・…＝見積の表と同じ列幅）⑤ 本文（読むだけ）
 *     自信の低い欄・行は淡い地＋薄墨の「確かめて」（色だけにしない）。金額は表示だけ（数量×単価を会社の丸めで。正は見積にしたあとの DB の値）
 *   下の段（S4＝§4.6 ④）：1行（64px）。左＝注意書き（ⓘ・折り返す）、右＝◉ 新しい見積を作成 ／ ○ 下書きの見積に追加［下書きの見積］［見積を作成］。
 *     1100px 未満は2行（上＝注意書き・下＝選択と主ボタン）
 *   検算（S4 ③）：金額は右寄せ・等幅。数量×単価（会社の明細の丸め＝contract の receivedLineAmount）が読み取った金額と違う行は朱・700＋下に「読み取り n」。
 *     単価が書類に無く 金額÷数量 で入れた単価（unsure）は淡い地（人が単価を直すと外す）。明細の段の右上に「読み取った合計 n円　行の合計 n円」と ✓ 検算 一致／⚠ 検算 不一致。
 *     人が数量・単価を直すたびに画面が同じ関数で計算し直す（API の normalize.ts と同じ）。行の合計＝数量×単価（無ければ読み取った金額）の和
 *   スマホ（幅 767px 以下・スマホ版）では開かない（S4 ⑥＝ReceivedBox が「読み取り済み（確認は PC で）」の文字だけにする）。幅 767px 以下の形は部品として残す
 *   読み取り中に開いたら中央に回る丸＋「AI読み取り中…　約30秒」
 *   区分と階層の検算（S5＝§4.7、R5 §4）：No の右に「区分」の列（72px のセレクト：明細／見出し／説明／率／別途／値引き）。小計・合計・消費税・内訳の合計の行は
 *     薄墨の文字（押すとセレクト＝「明細」などに変えられる・元の区分にも戻せる）で行ごと薄墨、取り込まない。見出し＝太字・淡い地・数量〜金額は空、説明＝薄墨、
 *     別途＝金額の欄に「別途」、値引き＝マイナス、率＝名称の右に淡い地の「8%」・読み取った金額。紙が2つ以上なら紙の切り替わりに紙の名前の帯。
 *     検算は contract の receivedCheckAll（API と同じ関数）で区分・数量・単価を直すたびに計算し直し、頭の1行の下に合わない段の文（朱 13px・3行まで・4行目からは「ほか n件」）
 */
/**
 * 行（API の行＋「確かめて」の行。rowUnsure＝信頼度が低い・数字が読めなかった行、unsure＝単価を 金額÷数量 で入れた）。
 * origKind＝読み取ったときの区分（検算のみの行を「明細」に変えても元に戻せるように）、kindOpen＝検算のみの行の文字を押してセレクトにした
 */
type Row = ReceivedReadLine & { rowUnsure: boolean; origKind: ReceivedLineKind; kindOpen: boolean };
/** 足した行：区分は明細・紙は前の行（最後の行）と同じ */
const blankRow = (costOwn: boolean, last: Row | undefined): Row => ({
  ...LineInput.parse({ id: crypto.randomUUID(), role: "detail", costOwn }),
  amountRead: null,
  computed: null,
  check: "none",
  unsure: false,
  rowUnsure: false,
  kind: "detail",
  sheetNo: last?.sheetNo ?? 0,
  sheetName: last?.sheetName ?? "",
  sheetKind: last?.sheetKind ?? "cover",
  rateText: null,
  origKind: "detail",
  kindOpen: false,
});
type Head = { subject: string; docNo: string; siteAddress: string; periodText: string; issueDate: string };

const lineKey = (l: ReceivedReadLine) => JSON.stringify([l.kind, l.name, l.spec, l.note, l.quantity, l.unit, l.unitPrice, l.itemId]);
/** 区分の「？」（見積の ROW_ROLE_HELP の 小計・別途・値引き と、読み取りの率・内訳の合計・検算のみ） */
const KIND_HELP = (
  <>
    <span style={{ display: "block" }}>{ROW_ROLE_HELP.subtotal}</span>
    <span style={{ display: "block" }}>{t(RECEIVED_READ_MSG.kindHelpRate)}</span>
    <span style={{ display: "block" }}>{ROW_ROLE_HELP.excluded}</span>
    <span style={{ display: "block" }}>{ROW_ROLE_HELP.discount}</span>
    <span style={{ display: "block" }}>{RECEIVED_READ_MSG.kindHelpSheetTotal}</span>
    <span style={{ display: "block" }}>{RECEIVED_READ_MSG.kindHelpCheckOnly}</span>
  </>
);
/** 紙の名前の帯の文字（表紙は名前が無ければ「表紙」、内訳は「内訳」） */
const sheetLabel = (x: Pick<Row, "sheetName" | "sheetKind">) => x.sheetName.trim() || (x.sheetKind === "cover" ? RECEIVED_READ_MSG.sheetCover : RECEIVED_READ_MSG.sheetBreakdown);
/** 検算の文は3行まで（4行目からは「ほか n件」） */
const NOTES_MAX = 3;

/** 境（原本の割合 %）：既定 40・30〜70・矢印は 5% ずつ。端末に覚える（gb.rd.split）。動きは components/SplitPanes.tsx の useSplit（#198 で外注見積の詳細の画面と共有） */
const SPLIT_KEY = "gb.rd.split";
const SPLIT_DEFAULT = 40;

export function ReceivedReadDrawer({
  doc,
  onClose,
  onChanged,
  toast,
}: {
  doc: ReceivedDocument | null;
  onClose: () => void;
  /** 種類・読み取りの状態が変わった（一覧を読み直す） */
  onChanged: () => void;
  toast: ToastFn;
}) {
  const id = doc?.id;
  const ref = useRef<HTMLDivElement>(null);
  const narrow = useNarrow();
  const qc = useQueryClient();
  const nav = useNavigate();
  const me = useMe();
  const showItems = uses(me.data, "items");
  const view = useQuery({
    queryKey: ["received-read", id],
    queryFn: () => api<ReceivedReadView>("GET", `/received-documents/${id}/read`),
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
  const fresh = useQuery({
    queryKey: ["received-doc", id],
    queryFn: () => api<ReceivedDocument>("GET", `/received-documents/${id}`),
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
  const d = fresh.data && fresh.data.id === id ? fresh.data : doc;
  const v = view.data && view.data.id === id ? view.data : null;
  const r = v?.result ?? null;

  // ---- 直す欄（読み取った内容が来たら入れ直す） ----
  const [head, setHead] = useState<Head>({ subject: "", docNo: "", siteAddress: "", periodText: "", issueDate: "" });
  const [rows, setRows] = useState<Row[]>([]);
  const base = useRef<Map<string, string>>(new Map());
  const [target, setTarget] = useState<"new" | "append">("new");
  const [draftId, setDraftId] = useState("");
  /** 請求書を仕入に（#233 3a）：{vendor}と{order}（差出人で引いた{vendor}・その{vendor}の候補の先頭が既定） */
  const [pVendor, setPVendor] = useState("");
  const [pOrder, setPOrder] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [origOpen, setOrigOpen] = useState(!narrow);
  const [redoAsk, setRedoAsk] = useState(false);
  // 原本の倍率（引き出しを閉じるまで覚える）・今のページ・境
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [pageInfo, setPageInfo] = useState<{ n: number; m: number }>({ n: 1, m: doc?.pageCount ?? 1 });
  const onPage = useCallback((n: number, m: number) => setPageInfo((p) => (p.n === n && p.m === m ? p : { n, m })), []);
  const wide = useWide(1100);
  const sp = useSplit({ storageKey: SPLIT_KEY, def: SPLIT_DEFAULT });
  const { split, dragging } = sp;
  const putSplit = sp.put;
  const body = sp.bodyRef;
  useEffect(() => {
    if (!r) return;
    setHead({ subject: r.subject, docNo: r.docNo, siteAddress: r.deliverTo, periodText: r.periodText, issueDate: r.issueDate ?? "" });
    setRows(r.lines.map((l, i) => ({ ...l, rowUnsure: r.checks.lines[i] ?? false, origKind: l.kind, kindOpen: false })));
    base.current = new Map(r.lines.map((l) => [l.id, lineKey(l)]));
    setErr(undefined);
    setStale(false);
  }, [r]);
  useEffect(() => {
    const first = v?.drafts[0]?.id ?? "";
    setDraftId((cur) => (v?.drafts.some((x) => x.id === cur) ? cur : first));
  }, [v?.drafts]);
  useEffect(() => {
    if (!v?.purchase) return;
    const vid = v.purchase.vendor?.id ?? "";
    setPVendor(vid);
    setPOrder(v.purchase.orders.find((o) => o.vendorId === vid)?.id ?? "");
  }, [v?.purchase]);
  useEffect(() => {
    if (id) setOrigOpen(!narrow);
  }, [id, narrow]);
  // 閉じたら倍率を忘れる（端末には覚えない）
  useEffect(() => {
    setZoom("fit");
    setPageInfo({ n: 1, m: doc?.pageCount ?? 1 });
  }, [id]);

  // ---- Esc で閉じる・開いたら枠にフォーカス ----
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".dim") || document.querySelector(".pop.pop-drawer") || document.querySelector(".calpop") || document.body.classList.contains("pick-open")) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [doc, onClose]);
  useEffect(() => {
    if (doc) ref.current?.focus();
  }, [doc?.id]);

  // ---- 種類 ----
  const changeKind = async (kind: ReceivedKind) => {
    if (!d) return;
    try {
      const next = await api<ReceivedDocument>("PATCH", `/received-documents/${d.id}`, { kind });
      qc.setQueryData(["received-doc", d.id], next);
      onChanged();
      toast(RECEIVED_MSG.kindToast.replace("{kind}", RECEIVED_KIND_LABEL[kind]));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const guess = r?.kindGuess === "order" || r?.kindGuess === "inquiry" ? r.kindGuess : null;
  const suggestKind = guess && d && d.kind !== guess ? guess : null;

  // ---- 行 ----
  const upd = (i: number, patch: Partial<Row>) => setRows((xs) => xs.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const addLine = () => setRows((xs) => [...xs, blankRow(xs[0]?.costOwn ?? true, xs[xs.length - 1])]);
  /**
   * 区分を変える。明細にした行で 数量・単価が無く読み取った金額があれば 数量 1・式・単価＝金額（確かめて＝淡い地。API の正規化と同じ）。
   * 別途にした行の金額の欄の文字が無ければ「別途」
   */
  const setKind = (i: number, kind: ReceivedLineKind) =>
    setRows((xs) =>
      xs.map((x, k) => {
        if (k !== i) return x;
        const next: Row = { ...x, kind, kindOpen: x.kindOpen || receivedCheckOnly(x.origKind) };
        if (kind === "detail" && x.quantity == null && x.unitPrice == null && x.amountRead != null && x.amountRead > 0) {
          Object.assign(next, { quantity: 1, unit: x.unit || "式", unitPrice: x.amountRead, unsure: true });
        }
        if (kind === "excluded" && !x.amountLabel) next.amountLabel = RECEIVED_LINE_KIND_LABEL.excluded;
        return next;
      }),
    );
  const delLine = (i: number) => setRows((xs) => xs.filter((_, k) => k !== i));
  const corrected = useMemo(() => {
    const now = new Set(rows.map((x) => x.id));
    const changed = rows.filter((x) => base.current.get(x.id) !== lineKey(x)).length; // 変えた行・足した行
    const removed = [...base.current.keys()].filter((k) => !now.has(k)).length;
    return changed + removed;
  }, [rows]);

  // ---- 候補の引き出し（この書類の候補 → 前の見積・品目。GET /estimates/suggest?q=） ----
  const [pick, setPick] = useState<{ row: number; q: string; tab: PickTab; sel: number } | null>(null);
  const sugg = useQuery({
    queryKey: ["received-suggest", pick?.q ?? ""],
    queryFn: () => api<Suggest>("GET", `/estimates/suggest?q=${encodeURIComponent(pick?.q ?? "")}`),
    enabled: !!pick,
    staleTime: 30_000,
  });
  const groups = useMemo(() => {
    const own: ReceivedCandidate[] = pick && v ? (v.candidates[r?.lines.findIndex((l) => l.id === rows[pick.row]?.id) ?? -1] ?? []) : [];
    const seen = new Set<string>();
    const past: Cand[] = [];
    const item: Cand[] = [];
    const push = (c: Cand) => {
      const k = `${c.src}|${c.name}|${"spec" in c ? c.spec : ""}|${"unit" in c ? c.unit : ""}`;
      if (seen.has(k)) return;
      seen.add(k);
      (c.src === "item" ? item : past).push(c);
    };
    for (const c of own) {
      if (c.source === "item" && c.itemId) push({ src: "item", id: c.itemId, name: c.name, unit: c.unit, taxCategory: "standard" });
      else push({ src: "past", name: c.name, spec: c.spec, unit: c.unit, price: c.unitPrice, cost: null });
    }
    for (const p of sugg.data?.past ?? []) push({ src: "past", name: p.name, spec: p.spec, unit: p.unit, price: p.unitPrice, cost: null, count: p.count, last: p.lastIssueDate, taxCategory: p.taxCategory });
    if (showItems) for (const it of sugg.data?.items ?? []) push({ src: "item", id: it.id, name: it.name, unit: it.unit, taxCategory: it.taxCategory });
    return { here: [] as Cand[], past, item, rate: [] as Cand[] };
  }, [pick, v, r, rows, sugg.data, showItems]);
  const pickList: Cand[] = !pick ? [] : pick.tab === "past" ? groups.past : pick.tab === "item" ? groups.item : [...groups.past, ...groups.item];
  const applyPick = (i: number) => {
    const c = pickList[i];
    if (!c || !pick) return;
    if (c.src === "item") upd(pick.row, { name: c.name, unit: c.unit || rows[pick.row]?.unit || "", itemId: c.id, taxCategory: c.taxCategory });
    else if (c.src === "past" || c.src === "here") upd(pick.row, { name: c.name, spec: c.spec, unit: c.unit || rows[pick.row]?.unit || "", ...(c.price != null ? { unitPrice: c.price, unsure: false } : {}), ...(c.taxCategory ? { taxCategory: c.taxCategory } : {}) });
    setPick(null);
  };

  // ---- 見積にする ----
  const drafts = v?.drafts ?? [];
  const submit = async () => {
    if (!d || !v) return;
    if (!rows.length) return setErr(RECEIVED_READ_MSG.noLines);
    setBusy(true);
    setErr(undefined);
    try {
      const draft = drafts.find((x) => x.id === draftId);
      // 見積に入れるのは見積の行の形＋区分と紙（検算・確かめての印は送らない）。見積の区分に写すのは API（contract の receivedImportLines）
      const lines = rows.map(({ unsure: _u, rowUnsure: _r, computed: _c, check: _k, origKind: _o, kindOpen: _ko, ...l }) => {
        void [_u, _r, _c, _k, _o, _ko];
        return l;
      });
      const out = await api<ReceivedImportResult>("POST", `/received-documents/${d.id}/import`, {
        target,
        ...(target === "append" && draft ? { documentId: draft.id, version: draft.version } : {}),
        head: { subject: head.subject, periodText: head.periodText, siteAddress: head.siteAddress, deliverTo: head.siteAddress, issueDate: head.issueDate || null },
        lines,
        correctedLines: corrected,
      });
      onChanged();
      onClose();
      nav(`/estimates/${out.documentId}`, { state: { notice: out.message } });
    } catch (e) {
      if (e instanceof ApiError && e.problem.code === "ESTIMATE_STALE") setStale(true);
      else setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  /** 仕入を登録（#233 3a）：消費税は読み取った消費税の行の和（無ければ明細から計算＝省く） */
  const isInvoice = d?.kind === "invoice" && !!v?.purchase;
  const pOrders = (v?.purchase?.orders ?? []).filter((o) => o.vendorId === pVendor);
  const submitPurchase = async () => {
    if (!d || !v) return;
    if (!rows.length) return setErr(RECEIVED_READ_MSG.noLines);
    if (!pVendor) return setErr(t(PURCHASE_MSG.receivedVendorRequired));
    setBusy(true);
    setErr(undefined);
    try {
      const lines = rows.map(({ unsure: _u, rowUnsure: _r, computed: _c, check: _k, origKind: _o, kindOpen: _ko, ...l }) => {
        void [_u, _r, _c, _k, _o, _ko];
        return l;
      });
      const taxRows = rows.filter((x) => x.kind === "tax" && x.amountRead != null);
      const out = await api<ReceivedImportResult>("POST", `/received-documents/${d.id}/import`, {
        target: "purchase",
        vendorId: pVendor,
        orderId: pOrder || null,
        ...(taxRows.length ? { taxAmount: taxRows.reduce((a, x) => a + (x.amountRead ?? 0), 0) } : {}),
        head: { subject: head.subject, periodText: head.periodText, issueDate: head.issueDate || null, docNo: head.docNo },
        lines,
        correctedLines: corrected,
      });
      onChanged();
      onClose();
      nav(`/purchases/${out.documentId}`, { state: { notice: out.message } });
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const reloadDrafts = async () => {
    setStale(false);
    await view.refetch();
  };

  // ---- やり直す ----
  const redo = async () => {
    if (!d) return;
    setRedoAsk(false);
    try {
      await api<ReceivedDocument>("POST", `/received-documents/${d.id}/read`);
      onChanged();
      onClose();
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };

  const title = d?.title ?? "";
  const closeBtn = (
    <button type="button" className="btn" onClick={onClose} data-testid="received-read-close">
      {RECEIVED_MSG.close}
    </button>
  );
  const redoBtn = (
    <button type="button" className="btn text" onClick={() => setRedoAsk(true)} disabled={!r} data-testid="received-read-redo">
      {RECEIVED_READ_MSG.redo}
    </button>
  );
  const unsure = <span className="rdr-unsure">{RECEIVED_READ_MSG.unsure}</span>;
  // 検算（画面で計算し直す＝API と同じ関数・同じ丸め。数量×単価は明細だけ）
  const code = v?.lineRounding ?? "round";
  const amountOf = (x: Row) => (x.kind === "detail" ? receivedLineAmount(x.quantity, x.unitPrice, code) : null);
  const totalCheck = receivedCheckAll(
    rows.map((x) => ({ ...x, computed: amountOf(x) })),
    { textRead: r?.total.textRead ?? null, taxIncludedHint: r?.taxIncluded ?? null },
    r?.total.taxRate ?? null,
  );
  const lineSum = totalCheck.sum;
  const totalRead = totalCheck.read;
  const sheetCount = new Set(rows.map((x) => x.sheetNo)).size;

  // ---- 境（原本の割合）：ドラッグ・矢印・ダブルクリック。1100px 以上は左右、未満は上下 ----
  const vertical = wide; // 境の線が縦（左右に並ぶ）
  // ドラッグだけにしない（WCAG 2.2 SC 2.5.7）：原本を広く（60／40）⇄ 内容を広く（40／60）
  const widen = split < 50;
  // S4 ②：アイコンだけ（押すたびに向きが変わる。aria-label と title は今の動作）
  const widenLabel = widen ? RECEIVED_READ_MSG.widenOriginal : RECEIVED_READ_MSG.widenContent;
  const widenBtn = narrow ? null : (
    <button type="button" className="rdp-tb rdp-ico" aria-label={widenLabel} title={widenLabel} onClick={() => putSplit(widen ? 100 - SPLIT_DEFAULT : SPLIT_DEFAULT)} data-testid="rd-widen" data-side={widen ? "original" : "content"}>
      <WidenIcon side={widen ? "original" : "content"} />
    </button>
  );
  const pageText = `P.${pageInfo.n}／${pageInfo.m}`;
  const bodyStyle = { "--rd-split": `${split}%` } as CSSProperties;

  return (
    <div ref={ref} tabIndex={-1} className={"vdrawer rdviewer rdread" + (doc ? " is-open" : "")} role="dialog" aria-label={`${RECEIVED_READ_MSG.drawerTitle} ${title}`} aria-hidden={!doc} data-testid="received-read-drawer">
      {narrow ? (
        <div className="dh rdv-dh2">
          <div className="rdv-row">
            <h2 className="rdr-title">
              <ScanIcon size={22} />
              {RECEIVED_READ_MSG.drawerTitle}
            </h2>
            <span className="sp" />
            {closeBtn}
          </div>
          <div className="rdv-row">
            <div className="rdv-name rdr-docname">{d ? <Trunc key={d.id}>{title}</Trunc> : null}</div>
            {d ? (
              <RowMenu
                label={`${title} の${RECEIVED_MSG.more}`}
                testId="received-read-more"
                popClass="pop-drawer"
                items={[
                  { label: RECEIVED_READ_MSG.redo, onSelect: () => setRedoAsk(true), testId: "received-read-redo" },
                  ...(d.format === "pdf" && d.originalUrl ? [{ label: RECEIVED_MSG.openOriginal, onSelect: () => window.open(d.originalUrl!, "_blank", "noopener"), testId: "received-read-open" }] : []),
                  ...(d.downloadUrl ? [{ label: RECEIVED_MSG.download, onSelect: () => (window.location.href = d.downloadUrl!), testId: "received-read-download" }] : []),
                ]}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div className="dh">
          <h2 className="rdr-title">
            <ScanIcon size={24} />
            {RECEIVED_READ_MSG.drawerTitle}
          </h2>
          <span className="rdr-docname" title={title}>
            {title}
          </span>
          <span className="sp" />
          {redoBtn}
          {closeBtn}
        </div>
      )}
      <div ref={body} className={"rdr-body" + (narrow ? " is-narrow" : vertical ? " is-cols" : " is-rows") + (dragging ? " is-dragging" : "")} style={bodyStyle}>
        <section className={"rdr-orig" + (origOpen ? "" : " is-collapsed")} aria-label={RECEIVED_READ_MSG.original}>
          {narrow ? (
            <button
              type="button"
              className="rdr-origtoggle"
              aria-expanded={origOpen}
              aria-label={`${origOpen ? RECEIVED_READ_MSG.originalHide : RECEIVED_READ_MSG.originalShow} ${pageText}`}
              onClick={() => setOrigOpen((o) => !o)}
              data-testid="received-read-orig-toggle"
            >
              <span className="rdr-origthumb" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
              <span className="rdr-origname">
                {RECEIVED_READ_MSG.original} <span className="num">{pageText}</span>
              </span>
              <span className={"rdr-chev" + (origOpen ? " is-open" : "")} aria-hidden="true" />
            </button>
          ) : null}
          {d && (origOpen || !narrow) ? <OriginalPane doc={d} zoom={zoom} onZoom={setZoom} compact={narrow} extra={widenBtn} onPage={onPage} /> : null}
        </section>
        {narrow ? null : (
          <div className="rdr-grip" {...sp.gripProps(vertical)} aria-label={RECEIVED_READ_MSG.splitter} data-testid="rd-split">
            <span className="rdr-griphandle" aria-hidden="true" />
          </div>
        )}
        <section className="rdr-read" aria-label={RECEIVED_READ_MSG.drawerTitle} data-testid="received-read-content">
          {view.isError ? (
            <ErrorText>{messageOf(view.error)}</ErrorText>
          ) : !v ? (
            <p className="hint">{RECEIVED_MSG.preparing}</p>
          ) : !r ? (
            v.status === "failed" ? (
              <p className="hint" data-testid="received-read-status">
                {`${RECEIVED_READ_MSG.failed}${v.error ? `（${v.error}）` : ""}`}
              </p>
            ) : (
              <p className="rdr-busy" role="status" data-testid="received-read-status">
                <SpinIcon />
                <span className="rdr-busy-t">{RECEIVED_READ_MSG.readingDrawer}</span>
                <span className="rd-eta">{RECEIVED_READ_MSG.etaHint}</span>
              </p>
            )
          ) : (
            <>
              <div className="rdr-kind">
                <label className="rdr-lab" htmlFor="rdr-kind">
                  {RECEIVED_READ_MSG.kind}
                </label>
                <select
                  id="rdr-kind"
                  className={"inp rdkind" + (d?.kind === "unsorted" ? " is-unsorted" : "")}
                  value={d?.kind ?? ""}
                  onChange={(e) => void changeKind(e.target.value as ReceivedKind)}
                  data-testid="received-read-kind"
                >
                  {RECEIVED_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {receivedKindOption(k)}
                    </option>
                  ))}
                </select>
              </div>
              {suggestKind ? (
                <p className="rdr-suggest" data-testid="received-read-kind-suggest">
                  {RECEIVED_READ_MSG.kindSuggest[suggestKind]}
                  <button type="button" className="btn text" onClick={() => void changeKind(suggestKind)} data-testid="received-read-kind-set">
                    {RECEIVED_READ_MSG.kindSet[suggestKind]}
                  </button>
                </p>
              ) : null}
              {v.sender.mismatch ? (
                <p className="rdr-warn" role="note" data-testid="received-read-sender-mismatch">
                  ⚠ {t(RECEIVED_READ_MSG.senderMismatch.replace("{sender}", r.sender.company))}
                </p>
              ) : null}
              <dl className="rdr-sender" data-testid="received-read-sender">
                <dt>{RECEIVED_READ_MSG.sender}</dt>
                <dd>{[r.sender.company, r.sender.person].filter(Boolean).join(" ") || "—"}</dd>
                {r.sender.tel ? (
                  <>
                    <dt>TEL</dt>
                    <dd className="num">{r.sender.tel}</dd>
                  </>
                ) : null}
                {r.sender.fax ? (
                  <>
                    <dt>FAX</dt>
                    <dd className="num">{r.sender.fax}</dd>
                  </>
                ) : null}
              </dl>
              <div className={"rdr-head" + (r.checks.head ? " is-unsure" : "")}>
                {r.checks.head ? <p className="rdr-headnote">{unsure}</p> : null}
                <Field label={RECEIVED_READ_MSG.date} id="rdr-date" hint={r.issueDate ? undefined : r.datedText || undefined}>
                  <DateInput id="rdr-date" label={RECEIVED_READ_MSG.date} value={head.issueDate} onChange={(x) => setHead((h) => ({ ...h, issueDate: x }))} testId="received-read-date" />
                </Field>
                <Field label={t("{subject}")} id="rdr-subject">
                  <input id="rdr-subject" className="inp" maxLength={200} value={head.subject} onChange={(e) => setHead((h) => ({ ...h, subject: e.target.value }))} data-testid="received-read-subject" />
                </Field>
                <Field label={RECEIVED_READ_MSG.docNo} id="rdr-docno">
                  <input id="rdr-docno" className="inp" maxLength={100} value={head.docNo} onChange={(e) => setHead((h) => ({ ...h, docNo: e.target.value }))} data-testid="received-read-docno" />
                </Field>
                <Field label={t("{site}")} id="rdr-site">
                  <input id="rdr-site" className="inp" maxLength={200} value={head.siteAddress} onChange={(e) => setHead((h) => ({ ...h, siteAddress: e.target.value }))} data-testid="received-read-site" />
                </Field>
                <Field label={t("{period}")} id="rdr-period">
                  <input id="rdr-period" className="inp" maxLength={100} value={head.periodText} onChange={(e) => setHead((h) => ({ ...h, periodText: e.target.value }))} data-testid="received-read-period" />
                </Field>
              </div>
              {lineSum != null || totalRead != null ? (
                <div className="rdr-total" data-testid="received-read-total">
                  <span className="rdr-total-t">
                    {totalRead != null && lineSum != null
                      ? RECEIVED_READ_MSG.totalLine(yen(totalRead), yen(lineSum))
                      : lineSum != null
                        ? RECEIVED_READ_MSG.sumOnly(yen(lineSum))
                        : RECEIVED_READ_MSG.readOnly(yen(totalRead ?? 0))}
                  </span>
                  {totalCheck.check === "ok" ? (
                    <span className="rdr-check is-ok" data-testid="received-read-total-check" data-check="ok">
                      <CheckIcon />
                      {totalCheck.taxIncluded ? RECEIVED_READ_MSG.checkOkTax : RECEIVED_READ_MSG.checkOk}
                    </span>
                  ) : totalCheck.check === "mismatch" ? (
                    <span className="rdr-check is-ng" data-testid="received-read-total-check" data-check="mismatch">
                      <WarnIcon size={16} />
                      {RECEIVED_READ_MSG.checkNg}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {totalCheck.notes.length ? (
                <ul className="rdr-notes" data-testid="received-read-check-notes">
                  {totalCheck.notes.slice(0, NOTES_MAX).map((n, k) => (
                    <li key={k} data-testid="received-read-check-note">
                      {n}
                    </li>
                  ))}
                  {totalCheck.notes.length > NOTES_MAX ? <li data-testid="received-read-check-more">{RECEIVED_READ_MSG.checkMore(totalCheck.notes.length - NOTES_MAX)}</li> : null}
                </ul>
              ) : null}
              <div className="rdr-tablewrap">
                <table className="rdr-lines" data-testid="received-read-lines">
                  <thead>
                    <tr>
                      <th className="c-no">{t(RECEIVED_READ_MSG.col.no)}</th>
                      <th className="c-kind">
                        <span className="rdr-kindhead">
                          {t(RECEIVED_READ_MSG.col.kind)}
                          <Help label={`${t(RECEIVED_READ_MSG.col.kind)}の説明`} text={KIND_HELP} testId="received-read-kind-help" />
                        </span>
                      </th>
                      <th className="c-name">{t(RECEIVED_READ_MSG.col.name)}</th>
                      <th className="c-pick" aria-label={RECEIVED_READ_MSG.pick} />
                      <th className="c-spec">{t(RECEIVED_READ_MSG.col.spec)}</th>
                      <th className="c-qty r">{t(RECEIVED_READ_MSG.col.qty)}</th>
                      <th className="c-unit">{t(RECEIVED_READ_MSG.col.unit)}</th>
                      <th className="c-price r">{t(RECEIVED_READ_MSG.col.price)}</th>
                      <th className="c-amt r">{t(RECEIVED_READ_MSG.col.amount)}</th>
                      <th className="c-note">{t(RECEIVED_READ_MSG.col.note)}</th>
                      <th className="c-more" aria-label={RECEIVED_READ_MSG.lineMenu} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((x, i) => {
                      const a = amountOf(x);
                      const isDetail = x.kind === "detail";
                      const checkOnly = receivedCheckOnly(x.kind);
                      const chk = isDetail ? receivedLineCheck(a, x.amountRead) : "none";
                      const band = sheetCount > 1 && (i === 0 || rows[i - 1]!.sheetNo !== x.sheetNo);
                      const cls = ["is-" + x.kind, x.rowUnsure ? "is-unsure" : "", checkOnly ? "is-checkonly" : ""].filter(Boolean).join(" ");
                      const label = RECEIVED_LINE_KIND_LABEL[x.kind];
                      // 金額の欄：明細＝数量×単価（S4）／率＝読み取った金額／値引き＝マイナス／別途＝金額の欄の文字／見出し・説明＝空／検算のみ＝読み取った金額（薄墨）
                      const shown =
                        isDetail ? (a == null ? "—" : yen(a))
                        : x.kind === "excluded" ? (x.amountLabel || RECEIVED_LINE_KIND_LABEL.excluded)
                        : x.kind === "discount" ? (x.amountRead == null ? "—" : yen(-Math.abs(x.amountRead)))
                        : x.kind === "rate" || checkOnly ? (x.amountRead == null ? "—" : yen(x.amountRead))
                        : "";
                      const nameInput = (
                        <input className="ce" aria-label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.name)}`} maxLength={200} value={x.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid="received-read-name" />
                      );
                      return (
                        <Fragment key={x.id}>
                          {band ? (
                            <tr className="rdr-sheet" data-testid="received-read-sheet">
                              <td colSpan={11}>{sheetLabel(x)}</td>
                            </tr>
                          ) : null}
                          <tr className={cls || undefined} data-testid="received-read-line" data-check={chk} data-kind={x.kind}>
                            <td className="c-no num">{i + 1}</td>
                            <td className="c-kind">
                              {checkOnly && !x.kindOpen ? (
                                <button type="button" className="rdr-kindtext" title={RECEIVED_READ_MSG.notTaken} aria-label={`${RECEIVED_READ_MSG.kindOf(i + 1)} ${label}（${RECEIVED_READ_MSG.notTaken}）`} onClick={() => upd(i, { kindOpen: true })} data-testid="received-read-kind-text">
                                  {label}
                                </button>
                              ) : (
                                <select className="rdr-kindsel" aria-label={RECEIVED_READ_MSG.kindOf(i + 1)} title={checkOnly ? RECEIVED_READ_MSG.notTaken : undefined} value={x.kind} onChange={(e) => setKind(i, e.target.value as ReceivedLineKind)} data-testid="received-read-kind-sel">
                                  {(receivedCheckOnly(x.origKind) ? [x.origKind, ...RECEIVED_LINE_KIND_SELECTABLE] : RECEIVED_LINE_KIND_SELECTABLE).map((k) => (
                                    <option key={k} value={k}>
                                      {RECEIVED_LINE_KIND_LABEL[k]}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                            <td className="c-name">
                              <span className="rdr-namecell">
                                {checkOnly ? <span className="rdr-plain" data-testid="received-read-name-text">{x.name || x.spec}</span> : nameInput}
                                {x.kind === "rate" && x.rateText ? (
                                  <span className="rdr-rate" data-testid="received-read-rate">
                                    {x.rateText}
                                  </span>
                                ) : null}
                                {x.rowUnsure ? <span className="rdr-unsure rdr-unsure-row">{RECEIVED_READ_MSG.unsure}</span> : null}
                              </span>
                            </td>
                            <td className="c-pick">
                              {checkOnly ? null : (
                                <button type="button" className="rdr-pick" aria-label={`${i + 1}行目の${RECEIVED_READ_MSG.pick}`} title={RECEIVED_READ_MSG.pick} onClick={() => setPick({ row: i, q: x.name, tab: "all", sel: 0 })} data-testid="received-read-pick">
                                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                                    <circle cx="7" cy="7" r="4.5" />
                                    <path d="M10.5 10.5 14 14" />
                                  </svg>
                                </button>
                              )}
                            </td>
                            <td className="c-spec">
                              {checkOnly ? (
                                <span className="rdr-plain">{x.name ? x.spec : ""}</span>
                              ) : (
                                <input className="ce" aria-label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.spec)}`} maxLength={200} value={x.spec} onChange={(e) => upd(i, { spec: e.target.value })} data-testid="received-read-spec" />
                              )}
                            </td>
                            <td className="c-qty">
                              {isDetail ? <NumInput kind="dec" aria-label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.qty)}`} value={x.quantity} onChange={(q) => upd(i, { quantity: q })} data-testid="received-read-qty" /> : null}
                            </td>
                            <td className="c-unit">
                              {isDetail ? <UnitInput className="ce" label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.unit)}`} value={x.unit} onChange={(u) => upd(i, { unit: u })} testId="received-read-unit" /> : null}
                            </td>
                            <td className={"c-price" + (isDetail && x.unsure ? " is-unsure" : "")} title={isDetail && x.unsure ? RECEIVED_READ_MSG.priceFromAmount : undefined} data-unsure={isDetail && x.unsure ? "1" : undefined}>
                              {isDetail ? <NumInput kind="int" aria-label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.price)}`} value={x.unitPrice} onChange={(p) => upd(i, { unitPrice: p, unsure: false })} data-testid="received-read-price" /> : null}
                            </td>
                            <td className={"c-amt num r" + (chk === "mismatch" ? " is-ng" : "")} data-testid="received-read-amount">
                              <span className="rdr-amt">{shown}</span>
                              {isDetail && x.amountRead != null && chk !== "ok" ? (
                                <span className="rdr-amtread" data-testid="received-read-amount-read">
                                  {RECEIVED_READ_MSG.readAmount(yen(x.amountRead))}
                                </span>
                              ) : null}
                            </td>
                            <td className="c-note">
                              {checkOnly ? null : <input className="ce" aria-label={`${i + 1}行目の${t(RECEIVED_READ_MSG.col.note)}`} maxLength={200} value={x.note} onChange={(e) => upd(i, { note: e.target.value })} data-testid="received-read-note" />}
                            </td>
                            <td className="c-more">
                              <RowMenu
                                label={`${i + 1}行目の${RECEIVED_READ_MSG.lineMenu}`}
                                popClass="pop-drawer"
                                testId="received-read-line-more"
                                items={[{ label: RECEIVED_READ_MSG.deleteLine, onSelect: () => delLine(i), danger: true, testId: "received-read-line-delete" }]}
                              />
                            </td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button type="button" className="btn text rdr-add" onClick={addLine} data-testid="received-read-add">
                ＋ {RECEIVED_READ_MSG.addLine}
              </button>
              {r.bodyText ? (
                <div className="rdr-bodytext">
                  <div className="rdr-lab">{RECEIVED_READ_MSG.body}</div>
                  <p data-testid="received-read-body">{r.bodyText}</p>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
      {r ? (
        <div className={"rdr-foot" + (wide ? "" : " is-2row")} data-testid="received-read-foot" data-notice-above="">
          <p className="rdr-notice" data-testid="received-read-notice">
            <InfoIcon />
            <span>{isInvoice ? PURCHASE_MSG.receivedNotice : RECEIVED_READ_MSG.notice}</span>
          </p>
          {isInvoice ? (
            <div className="rdr-target" data-testid="received-read-purchase">
              <label className="rdr-pf">
                <span>{t(PURCHASE_MSG.receivedVendor)}</span>
                <select
                  className="inp rdr-draft"
                  value={pVendor}
                  onChange={(e) => {
                    setPVendor(e.target.value);
                    setPOrder(v?.purchase?.orders.find((o) => o.vendorId === e.target.value)?.id ?? "");
                  }}
                  data-testid="received-read-vendor"
                >
                  <option value="">{t(PURCHASE_MSG.receivedVendorPick)}</option>
                  {(v?.purchase?.vendors ?? []).map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </select>
              </label>
              <label className="rdr-pf">
                <span>{t(PURCHASE_MSG.receivedOrder)}</span>
                <select className="inp rdr-draft" value={pOrder} onChange={(e) => setPOrder(e.target.value)} data-testid="received-read-order">
                  <option value="">{t(PURCHASE_MSG.receivedNoOrder)}</option>
                  {pOrders.map((o) => (
                    <option key={o.id} value={o.id}>{[o.displayNo, o.groupName, o.subject].filter(Boolean).join(" ")}</option>
                  ))}
                </select>
              </label>
            </div>
          ) : (
          <div className="rdr-target">
            <label className="rdr-radio">
              <input type="radio" name="rdr-target" checked={target === "new"} onChange={() => setTarget("new")} data-testid="received-read-target-new" />
              {RECEIVED_READ_MSG.createNew}
            </label>
            <label className="rdr-radio">
              <input type="radio" name="rdr-target" checked={target === "append"} disabled={!drafts.length} onChange={() => setTarget("append")} data-testid="received-read-target-append" />
              {RECEIVED_READ_MSG.appendTo}
            </label>
            {drafts.length ? (
              <select className="inp rdr-draft" aria-label={RECEIVED_READ_MSG.appendTo} value={draftId} disabled={target !== "append"} onChange={(e) => setDraftId(e.target.value)} data-testid="received-read-draft">
                {drafts.map((x) => (
                  <option key={x.id} value={x.id}>
                    {(x.subject || "—") + `（${x.lineCount}行）`}
                  </option>
                ))}
              </select>
            ) : (
              <span className="muted" data-testid="received-read-no-drafts">{RECEIVED_READ_MSG.noDrafts}</span>
            )}
          </div>
          )}
          <div className="rdr-submit">
            {stale ? (
              <span className="rdr-stale" role="alert">
                {RECEIVED_READ_MSG.stale}
                <button type="button" className="btn text" onClick={() => void reloadDrafts()} data-testid="received-read-reload">
                  {RECEIVED_READ_MSG.reload}
                </button>
              </span>
            ) : null}
            {err ? <ErrorText>{err}</ErrorText> : null}
            {isInvoice ? (
              <button type="button" className="btn primary" disabled={busy || !rows.length || !pVendor} onClick={() => void submitPurchase()} data-testid="received-read-purchase-submit">
                {PURCHASE_MSG.receivedCreate}
              </button>
            ) : (
              <button type="button" className="btn primary" disabled={busy || !rows.length || (target === "append" && !draftId)} onClick={() => void submit()} data-testid="received-read-submit">
                {target === "new" ? RECEIVED_READ_MSG.create : RECEIVED_READ_MSG.append}
              </button>
            )}
          </div>
        </div>
      ) : null}
      {pick ? (
        <Picker
          open
          q={pick.q}
          tab={pick.tab}
          sel={pick.sel}
          multi={false}
          groups={groups}
          showItems={showItems}
          tabs={["all", "past", "item"]}
          hideMulti
          list={pickList}
          loading={sugg.isFetching}
          onQ={(q) => setPick((p) => (p ? { ...p, q, sel: 0 } : p))}
          onTab={(tab) => setPick((p) => (p ? { ...p, tab, sel: 0 } : p))}
          onMove={(dlt) => setPick((p) => (p ? { ...p, sel: Math.max(0, Math.min(pickList.length - 1, p.sel + dlt)) } : p))}
          onPick={applyPick}
          onMulti={() => undefined}
          onClose={() => setPick(null)}
        />
      ) : null}
      {redoAsk ? (
        <div className="dim rddim" onClick={(e) => e.target === e.currentTarget && setRedoAsk(false)}>
          <div className="dlg" role="alertdialog" aria-label={RECEIVED_READ_MSG.redo} data-testid="received-read-redo-dialog">
            <div className="dh">{RECEIVED_READ_MSG.redo}</div>
            <div className="db">
              <p style={{ margin: 0 }}>{RECEIVED_READ_MSG.redoBody}</p>
            </div>
            <div className="df">
              <button type="button" className="btn" onClick={() => setRedoAsk(false)} autoFocus>
                {RECEIVED_MSG.cancel}
              </button>
              <button type="button" className="btn primary" onClick={() => void redo()} data-testid="received-read-redo-confirm">
                決定
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
